import { Component } from '@theme/component';
import { morph } from '@theme/morph';

/**
 * Variant overlay for product cards.
 * Shows a "Choose your preference" bottom-sheet panel with variant options,
 * price, and an "Add to cart" button directly on the card.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [overlayPrice] - The price container inside the overlay.
 *
 * @extends {Component<Refs>}
 */
class VariantOverlay extends Component {
  connectedCallback() {
    super.connectedCallback();

    // Listen to radio changes to update variant ID + price
    this.addEventListener('change', this.#handleOptionChange);

    // Stop all click events within the overlay from propagating
    // to the parent product-card (prevents card navigation)
    this.addEventListener('click', this.#stopPropagation);

    // Listen for trigger buttons in parent product card (PLP) or Resmed predictive card
    this._card = this.closest('product-card') || this.closest('.predictive-product-card');
    if (this._card) {
      this._card.addEventListener('click', this.#handleTriggerClick);
    }

    // Close overlay when viewport crosses the mobile/desktop breakpoint
    this._mql = window.matchMedia('(max-width: 749px)');
    this._mql.addEventListener('change', this.#handleBreakpointChange);
    window.addEventListener('resize', this.#syncOverlayHeightVar);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('change', this.#handleOptionChange);
    this.removeEventListener('click', this.#stopPropagation);
    if (this._card) {
      this._card.removeEventListener('click', this.#handleTriggerClick);
    }
    if (this._mql) {
      this._mql.removeEventListener('change', this.#handleBreakpointChange);
    }
    this._panelResizeObserver?.disconnect();
    window.removeEventListener('resize', this.#syncOverlayHeightVar);
  }

  /** Writes open panel height to CSS var so floating widgets can offset dynamically. */
  #syncOverlayHeightVar = () => {
    const panel = /** @type {HTMLElement | null} */ (this.querySelector('.variant-overlay__panel'));
    const isOpen = this.hasAttribute('open') && !this.hasAttribute('hidden');
    const height = isOpen && panel ? panel.offsetHeight : 0;
    document.documentElement.style.setProperty('--variant-overlay-panel-height', `${height}px`);
  };

  /**
   * Stops click events from bubbling to the product card.
   * @param {Event} event
   */
  #stopPropagation = (event) => {
    event.stopPropagation();
  };

  /**
   * Closes the overlay when the viewport crosses the mobile/desktop breakpoint.
   * Prevents the overlay from staying open in the wrong layout mode.
   */
  #handleBreakpointChange = () => {
    if (this.hasAttribute('open')) {
      this.close();
    }
  };

  /**
   * Handles clicks on [data-variant-overlay-trigger] buttons in the parent card.
   * @param {Event} event
   */
  #handleTriggerClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const trigger = event.target.closest('[data-variant-overlay-trigger]');
    if (!trigger) return;

    event.preventDefault();
    event.stopPropagation();
    this.open();
  };

  /** Open the overlay */
  open() {
    // On mobile, move to document.body so position:fixed escapes
    // container-type / will-change containing blocks in the grid
    if (window.innerWidth <= 749 && !this._movedToBody) {
      this._originalParent = this.parentElement;
      this._originalNextSibling = this.nextSibling;
      document.body.appendChild(this);
      this._movedToBody = true;
      document.body.style.overflow = 'hidden';
    }
    this.removeAttribute('hidden');
    this.setAttribute('open', '');
    requestAnimationFrame(this.#syncOverlayHeightVar);

    const panel = /** @type {HTMLElement | null} */ (this.querySelector('.variant-overlay__panel'));
    if (panel && 'ResizeObserver' in window) {
      this._panelResizeObserver?.disconnect();
      this._panelResizeObserver = new ResizeObserver(this.#syncOverlayHeightVar);
      this._panelResizeObserver.observe(panel);
    }
  }

  /** Close the overlay */
  close() {
    this.setAttribute('hidden', '');
    this.removeAttribute('open');
    // Move back into the product card
    if (this._movedToBody && this._originalParent) {
      this._originalParent.insertBefore(this, this._originalNextSibling || null);
      this._movedToBody = false;
      document.body.style.overflow = '';
    }
    this._panelResizeObserver?.disconnect();
    this.#syncOverlayHeightVar();
  }

  /** Toggle */
  toggle() {
    if (this.hasAttribute('open')) {
      this.close();
    } else {
      this.open();
    }
  }

  /**
   * Handles radio input changes within the overlay.
   * Finds the matching variant from all combinations and updates the form.
   * @param {Event} event
   */
  #handleOptionChange = (event) => {
    if (!(event.target instanceof HTMLInputElement) || event.target.type !== 'radio') return;

    // If a direct variant-id is on the radio, use it
    const variantId = event.target.dataset.variantId;
    if (variantId) {
      this.#updateFormVariant(variantId, event.target.dataset.optionAvailable === 'true');
      this.#fetchUpdatedPrice(variantId);
    } else {
      // For multi-option products, we need to build the selected combination
      this.#resolveVariantFromOptions();
    }
  };

  /**
   * Resolves the variant from the currently selected option combination
   * by fetching the section rendering endpoint.
   */
  #resolveVariantFromOptions() {
    const selectedValues = [];
    const radios = /** @type {NodeListOf<HTMLInputElement>} */ (
      this.querySelectorAll('input[type="radio"]:checked')
    );
    for (const radio of radios) {
      if (radio.dataset.optionValueId) {
        selectedValues.push(radio.dataset.optionValueId);
      }
    }

    if (selectedValues.length === 0) return;

    const productId = this.dataset.productId;
    if (!productId) return;

    const productCard = this.closest('product-card');
    const productUrl = productCard?.querySelector('a[ref="productCardLink"]')?.getAttribute('href');
    if (!productUrl) return;

    const url = new URL(productUrl, window.location.origin);
    // Clear existing option_values params
    url.searchParams.delete('option_values[]');
    for (const val of selectedValues) {
      url.searchParams.append('option_values[]', val);
    }
    url.searchParams.set('section_id', 'section-rendering-product-card');

    fetch(url.href)
      .then((res) => res.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const priceContainer = doc.querySelector('product-price [ref="priceContainer"]');
        const variantInput = doc.querySelector('input[name="id"]');
        if (variantInput instanceof HTMLInputElement) {
          this.#updateFormVariant(variantInput.value, !variantInput.disabled);
        }
        if (priceContainer) {
          const currentPrice = this.querySelector('.variant-overlay__price');
          if (currentPrice) {
            const priceEl = priceContainer.querySelector('.price');
            if (priceEl) {
              const currentEl = currentPrice.querySelector('.variant-overlay__price-current');
              if (currentEl) {
                currentEl.textContent = priceEl.textContent?.trim() || '';
              }
            }
          }
        }
      })
      .catch(() => {
        // silently fail – the user can still browse
      });
  }

  /**
   * Updates the hidden form variant input.
   * @param {string} variantId
   * @param {boolean} available
   */
  #updateFormVariant(variantId, available) {
    const hiddenInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[name="id"]')
    );
    if (hiddenInput) {
      hiddenInput.value = variantId;
      hiddenInput.disabled = !available;
    }

    const addBtn = /** @type {HTMLButtonElement | null} */ (
      this.querySelector('.variant-overlay__add-btn')
    );
    if (addBtn) {
      addBtn.disabled = !available;
    }
  }

  /**
   * Fetches updated price for a given variant.
   * @param {string} variantId
   */
  #fetchUpdatedPrice(variantId) {
    const productCard = this.closest('product-card');
    const productUrl = productCard?.querySelector('a[ref="productCardLink"]')?.getAttribute('href');
    if (!productUrl) return;

    const url = new URL(productUrl, window.location.origin);
    url.searchParams.set('variant', variantId);
    url.searchParams.set('section_id', 'section-rendering-product-card');

    fetch(url.href)
      .then((res) => res.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const priceContainer = doc.querySelector('product-price [ref="priceContainer"]');
        if (priceContainer) {
          const priceEl = priceContainer.querySelector('.price');
          if (priceEl) {
            const currentEl = this.querySelector('.variant-overlay__price-current');
            if (currentEl) {
              currentEl.textContent = priceEl.textContent?.trim() || '';
            }
          }
        }
      })
      .catch(() => {});
  }
}

if (!customElements.get('variant-overlay')) {
  customElements.define('variant-overlay', VariantOverlay);
}
